import type { Db } from "../platform/db/open.ts";

// The Spend screen's reads of the spend ledger: totals and a daily trend,
// breakdowns by one dimension, the most expensive Requests and one Request's
// Tasks and Runs. Every figure is a sum over `model_usage` rows; nothing else
// is read, so an answer carries ids, copied names and numbers only.
// See ADR-0051 §6.

/** An inclusive range of UTC days, each written `YYYY-MM-DD`. */
export type SpendRange = { from: string; to: string };

/**
 * The sums over a set of ledger rows. `costMicros` adds the rows that have a
 * cost, so `unpricedCalls` says how many rows it leaves out; `cacheHitRatio`
 * is cache-read tokens over all input tokens, 0 when there was no input.
 */
export type SpendUsage = {
  costMicros: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  calls: number;
  unpricedCalls: number;
  cacheHitRatio: number;
};

/** One day's spend on one stage, for the trend. */
export type SpendDay = SpendUsage & { day: string; stage: string };

/** A range's totals, its daily trend by stage and the count and cost of rows from each cost source. */
export type SpendSummary = {
  range: SpendRange;
  totals: SpendUsage;
  daily: SpendDay[];
  sources: {
    reported: { calls: number; costMicros: number };
    priced: { calls: number; costMicros: number };
    unpriced: { calls: number; costMicros: null };
  };
};

/** The dimensions a breakdown can group by. */
export const SPEND_DIMENSIONS = ["organisation", "project", "stage", "runtime", "model"] as const;

/** One dimension a breakdown groups by. */
export type SpendDimension = (typeof SPEND_DIMENSIONS)[number];

/**
 * One group of a breakdown. `key` is the id or value grouped on and `label`
 * its copied name (the value itself for stage, runtime and model); `provider`
 * is present on model rows only.
 */
export type SpendBreakdownRow = SpendUsage & { key: string | null; label: string | null; provider?: string | null };

/** A range's spend grouped by one dimension, most expensive first. */
export type SpendBreakdown = { by: SpendDimension; range: SpendRange; rows: SpendBreakdownRow[] };

/** A Request's spend with its Organisation and Project, under the names copied onto its latest row. */
export type SpendRequestRow = SpendUsage & {
  requestId: string;
  // Null once the Request is deleted.
  requestTitle: string | null;
  organisationId: string | null;
  organisationName: string | null;
  projectId: string | null;
  projectName: string | null;
};

/** The most expensive Requests in a range. */
export type SpendTopRequests = { range: SpendRange; rows: SpendRequestRow[] };

/** One Run's spend: what it was, what it called, and its first and last report. */
export type SpendRun = SpendUsage & {
  runId: string;
  runKind: string | null;
  attemptNo: number | null;
  stage: string;
  runtime: string;
  provider: string | null;
  model: string;
  firstAt: string;
  lastAt: string;
};

/** One Task's spend and its Runs; `taskId` is null for the Request's Runs that belong to no Task. */
export type SpendTask = SpendUsage & { taskId: string | null; runs: SpendRun[] };

/** Spend outside any Run (refinement, the pull request description) for one stage, runtime and model. */
export type SpendConversation = SpendUsage & {
  stage: string;
  runtime: string;
  provider: string | null;
  model: string;
  firstAt: string;
  lastAt: string;
};

/** One Request's whole spend, per Task and Run, with its conversations beside them. */
export type SpendRequest = Omit<SpendRequestRow, keyof SpendUsage> & {
  totals: SpendUsage;
  tasks: SpendTask[];
  conversations: SpendConversation[];
};

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

function validDay(v: string | null): string | null {
  if (v === null) return null;
  const m = DAY.exec(v);
  if (!m) return null;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v ? v : null;
}

function dayOf(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Reads a range from `from` and `to` query values. A missing or malformed
 * value falls back to the thirty days ending on `now`'s UTC day.
 */
export function spendRangeOf(from: string | null, to: string | null, now: Date): SpendRange {
  const end = validDay(to) ?? dayOf(now);
  const start = validDay(from) ?? dayOf(new Date(Date.parse(`${end}T00:00:00Z`) - 29 * 86_400_000));
  return { from: start, to: end };
}

function rangeParams(r: SpendRange): [string, string] {
  const next = new Date(Date.parse(`${r.to}T00:00:00Z`) + 86_400_000);
  return [`${r.from}T00:00:00Z`, next.toISOString()];
}

const IN_RANGE = `occurred_at >= ?::timestamptz AND occurred_at < ?::timestamptz`;

const USAGE_COLUMNS = `
  COALESCE(SUM(cost_micros), 0) AS cost_micros,
  COALESCE(SUM(input_tokens), 0) AS input_tokens,
  COALESCE(SUM(output_tokens), 0) AS output_tokens,
  COALESCE(SUM(cache_read_tokens), 0) AS cache_read_tokens,
  COALESCE(SUM(cache_write_tokens), 0) AS cache_write_tokens,
  COUNT(*) AS calls,
  COUNT(*) FILTER (WHERE cost_source = 'unpriced') AS unpriced_calls`;

type UsageRow = {
  cost_micros: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  calls: number;
  unpriced_calls: number;
};

function usageOf(r: UsageRow): SpendUsage {
  const allInput = r.input_tokens + r.cache_read_tokens + r.cache_write_tokens;
  return {
    costMicros: r.cost_micros,
    inputTokens: r.input_tokens,
    outputTokens: r.output_tokens,
    cacheReadTokens: r.cache_read_tokens,
    cacheWriteTokens: r.cache_write_tokens,
    calls: r.calls,
    unpricedCalls: r.unpriced_calls,
    cacheHitRatio: allInput === 0 ? 0 : r.cache_read_tokens / allInput,
  };
}

// The model with its provider's `<provider>/` prefix stripped, regardless of
// case: live pi rows name the model bare and the backfill names it prefixed,
// so grouping on this keeps one model in one row. Mirrors `bareModel`.
const BARE_MODEL = `CASE WHEN provider IS NOT NULL AND starts_with(lower(model), lower(provider) || '/')
  THEN substr(model, length(provider) + 2) ELSE model END`;

const latest = (column: string): string => `(array_agg(${column} ORDER BY occurred_at DESC))[1]`;

/** Reads a range's totals, its daily trend split by stage, and the count and cost of each cost source. */
export async function readSpendSummary(db: Db, range: SpendRange): Promise<SpendSummary> {
  const params = rangeParams(range);
  const totals = (await db
    .prepare(`SELECT ${USAGE_COLUMNS} FROM model_usage WHERE ${IN_RANGE}`)
    .get(...params)) as UsageRow;
  const daily = (await db
    .prepare(
      `SELECT to_char(occurred_at AT TIME ZONE 'UTC', 'YYYY-MM-DD') AS day, stage, ${USAGE_COLUMNS}
         FROM model_usage WHERE ${IN_RANGE}
        GROUP BY day, stage ORDER BY day, stage`,
    )
    .all(...params)) as (UsageRow & { day: string; stage: string })[];
  const sources = (await db
    .prepare(
      `SELECT cost_source, COUNT(*) AS calls, COALESCE(SUM(cost_micros), 0) AS cost_micros
         FROM model_usage WHERE ${IN_RANGE} GROUP BY cost_source`,
    )
    .all(...params)) as { cost_source: string; calls: number; cost_micros: number }[];
  const source = (name: string) => sources.find((s) => s.cost_source === name);
  return {
    range,
    totals: usageOf(totals),
    daily: daily.map((d) => ({ day: d.day, stage: d.stage, ...usageOf(d) })),
    sources: {
      reported: { calls: source("reported")?.calls ?? 0, costMicros: source("reported")?.cost_micros ?? 0 },
      priced: { calls: source("priced")?.calls ?? 0, costMicros: source("priced")?.cost_micros ?? 0 },
      unpriced: { calls: source("unpriced")?.calls ?? 0, costMicros: null },
    },
  };
}

/** Whether a query value names a breakdown dimension. */
export function isSpendDimension(v: unknown): v is SpendDimension {
  return typeof v === "string" && (SPEND_DIMENSIONS as readonly string[]).includes(v);
}

const GROUPING: Record<SpendDimension, { key: string; label: string; provider: boolean }> = {
  organisation: { key: "organisation_id", label: latest("organisation_name"), provider: false },
  project: { key: "project_id", label: latest("project_name"), provider: false },
  stage: { key: "stage", label: "stage", provider: false },
  runtime: { key: "runtime", label: "runtime", provider: false },
  model: { key: BARE_MODEL, label: BARE_MODEL, provider: true },
};

/** Reads a range's spend grouped by one dimension, most expensive first; the model breakdown keeps unpriced models. */
export async function readSpendBreakdown(db: Db, by: SpendDimension, range: SpendRange): Promise<SpendBreakdown> {
  const g = GROUPING[by];
  const groupBy = g.provider ? `provider, ${g.key}` : g.key;
  const rows = (await db
    .prepare(
      `SELECT ${g.key} AS key, ${g.label} AS label${g.provider ? ", provider" : ""}, ${USAGE_COLUMNS}
         FROM model_usage WHERE ${IN_RANGE}
        GROUP BY ${groupBy}
        ORDER BY cost_micros DESC, calls DESC, key`,
    )
    .all(...rangeParams(range))) as (UsageRow & { key: string | null; label: string | null; provider?: string | null })[];
  return {
    by,
    range,
    rows: rows.map((r) => ({
      key: r.key,
      label: r.label,
      ...(g.provider ? { provider: r.provider ?? null } : {}),
      ...usageOf(r),
    })),
  };
}

type RequestNamesRow = {
  request_id: string;
  request_title: string | null;
  organisation_id: string | null;
  organisation_name: string | null;
  project_id: string | null;
  project_name: string | null;
};

const REQUEST_NAMES = `
  request_id,
  ${latest("request_title")} AS request_title,
  ${latest("organisation_id")} AS organisation_id,
  ${latest("organisation_name")} AS organisation_name,
  ${latest("project_id")} AS project_id,
  ${latest("project_name")} AS project_name`;

function namesOf(r: RequestNamesRow): Omit<SpendRequestRow, keyof SpendUsage> {
  return {
    requestId: r.request_id,
    requestTitle: r.request_title,
    organisationId: r.organisation_id,
    organisationName: r.organisation_name,
    projectId: r.project_id,
    projectName: r.project_name,
  };
}

/** Reads the `limit` most expensive Requests in a range, with their Organisation and Project names. */
export async function readTopSpendRequests(db: Db, range: SpendRange, limit: number): Promise<SpendTopRequests> {
  const rows = (await db
    .prepare(
      `SELECT ${REQUEST_NAMES}, ${USAGE_COLUMNS}
         FROM model_usage WHERE request_id IS NOT NULL AND ${IN_RANGE}
        GROUP BY request_id
        ORDER BY cost_micros DESC, calls DESC, request_id
        LIMIT ?`,
    )
    .all(...rangeParams(range), limit)) as (UsageRow & RequestNamesRow)[];
  return { range, rows: rows.map((r) => ({ ...namesOf(r), ...usageOf(r) })) };
}

type RunRow = UsageRow & {
  task_id: string | null;
  run_id: string;
  run_kind: string | null;
  attempt_no: number | null;
  stage: string;
  runtime: string;
  provider: string | null;
  model: string;
  first_at: string;
  last_at: string;
};

/**
 * Reads one Request's whole spend: its totals, its spend per Task with each
 * Run's kind, attempt, model, runtime, tokens, cost and first and last
 * report, and its spend outside any Run. Null when the ledger has no row for it.
 */
export async function readSpendRequest(db: Db, requestId: string): Promise<SpendRequest | null> {
  const head = (await db
    .prepare(`SELECT ${REQUEST_NAMES}, ${USAGE_COLUMNS} FROM model_usage WHERE request_id = ? GROUP BY request_id`)
    .get(requestId)) as (UsageRow & RequestNamesRow) | undefined;
  if (!head) return null;
  const runs = (await db
    .prepare(
      `SELECT task_id, run_id, run_kind, attempt_no, stage, runtime, provider, ${BARE_MODEL} AS model,
              MIN(occurred_at) AS first_at, MAX(occurred_at) AS last_at, ${USAGE_COLUMNS}
         FROM model_usage WHERE request_id = ? AND run_id IS NOT NULL
        GROUP BY task_id, run_id, run_kind, attempt_no, stage, runtime, provider, ${BARE_MODEL}
        ORDER BY first_at, run_id`,
    )
    .all(requestId)) as RunRow[];
  const taskTotals = (await db
    .prepare(
      `SELECT task_id, MIN(occurred_at) AS first_at, ${USAGE_COLUMNS}
         FROM model_usage WHERE request_id = ? AND run_id IS NOT NULL
        GROUP BY task_id ORDER BY first_at, task_id`,
    )
    .all(requestId)) as (UsageRow & { task_id: string | null })[];
  const conversations = (await db
    .prepare(
      `SELECT stage, runtime, provider, ${BARE_MODEL} AS model,
              MIN(occurred_at) AS first_at, MAX(occurred_at) AS last_at, ${USAGE_COLUMNS}
         FROM model_usage WHERE request_id = ? AND run_id IS NULL
        GROUP BY stage, runtime, provider, ${BARE_MODEL}
        ORDER BY first_at, stage`,
    )
    .all(requestId)) as (UsageRow & Omit<RunRow, "task_id" | "run_id" | "run_kind" | "attempt_no">)[];
  return {
    ...namesOf(head),
    totals: usageOf(head),
    tasks: taskTotals.map((t) => ({
      taskId: t.task_id,
      ...usageOf(t),
      runs: runs
        .filter((r) => r.task_id === t.task_id)
        .map((r) => ({
          runId: r.run_id,
          runKind: r.run_kind,
          attemptNo: r.attempt_no,
          stage: r.stage,
          runtime: r.runtime,
          provider: r.provider,
          model: r.model,
          ...usageOf(r),
          firstAt: r.first_at,
          lastAt: r.last_at,
        })),
    })),
    conversations: conversations.map((c) => ({
      stage: c.stage,
      runtime: c.runtime,
      provider: c.provider,
      model: c.model,
      ...usageOf(c),
      firstAt: c.first_at,
      lastAt: c.last_at,
    })),
  };
}
